import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './language-picker.css';

type Option = { value: string; label: string; tag: string };
type Props = {
  value: string;
  options: readonly Option[];
  label: string;
  disabled?: boolean;
  onChange: (value: string) => void;
};

function Symbol({ kind }: { kind: 'globe' | 'chevron' | 'check' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {kind === 'globe' ? (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3a19 19 0 0 1 0 18 19 19 0 0 1 0-18Z" />
        </>
      ) : kind === 'chevron' ? (
        <path d="m8 10 4 4 4-4" />
      ) : (
        <path d="m5 12 4 4L19 6" />
      )}
    </svg>
  );
}

/** Shared by the portal, Logto sign-in and account preferences. */
export function LanguagePicker({ value, options, label, disabled, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 180, height: 400 });
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();
  const current = options.find((item) => item.value === value) ?? options[0];
  const close = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) trigger.current?.focus();
  };

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const rect = trigger.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(180, window.innerWidth - 24);
      const height = Math.min(520, window.innerHeight - 24);
      const below = window.innerHeight - rect.bottom - 12;
      const top =
        below >= Math.min(280, height) ? rect.bottom + 8 : Math.max(12, rect.top - height - 8);
      const left = document.documentElement.dir === 'rtl' ? rect.left : rect.right - width;
      setPosition({
        top,
        left: Math.max(12, Math.min(left, window.innerWidth - width - 12)),
        width,
        height: Math.min(height, window.innerHeight - top - 12),
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const selected = panel.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]');
    (selected ?? panel.current)?.focus({ preventScroll: true });
    selected?.scrollIntoView({ block: 'nearest' });
    const outside = (event: Event) => {
      if (
        !panel.current?.contains(event.target as Node) &&
        !trigger.current?.contains(event.target as Node)
      )
        setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('focusin', outside);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('focusin', outside);
    };
  }, [open]);

  return (
    <div className="language-picker">
      <button
        ref={trigger}
        data-language={current?.tag}
        className="language-trigger"
        type="button"
        aria-label={label}
        aria-description={current?.label}
        title={current?.label}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        disabled={disabled}
        onClick={() => {
          setOpen(!open);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <Symbol kind="globe" />
        <span className="language-trigger-label" lang={current?.tag} dir="auto">
          {current?.label}
        </span>
        <Symbol kind="chevron" />
      </button>
      {open &&
        createPortal(
          <div
            ref={panel}
            id={id}
            className="language-panel"
            role="dialog"
            aria-label={label}
            tabIndex={-1}
            style={{
              top: position.top,
              left: position.left,
              width: position.width,
              maxHeight: position.height,
            }}
            onKeyDown={(event) => {
              if (event.key === 'Tab') {
                close(true);
                return;
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                close(true);
                return;
              }
              const items = Array.from(
                panel.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? [],
              );
              if (!items.length) return;
              const index = items.indexOf(document.activeElement as HTMLButtonElement);
              let next: number;
              if (event.key === 'ArrowDown') next = (index + 1) % items.length;
              else if (event.key === 'ArrowUp') next = index <= 0 ? items.length - 1 : index - 1;
              else if (event.key === 'Home') next = 0;
              else if (event.key === 'End') next = items.length - 1;
              else return;
              event.preventDefault();
              items[next]?.focus();
              items[next]?.scrollIntoView({ block: 'nearest' });
            }}
          >
            <div className="language-options" role="listbox" aria-label={label}>
              {options.map((item) => (
                <button
                  key={item.value}
                  className="language-option"
                  type="button"
                  role="option"
                  data-language={item.tag}
                  aria-selected={item.value === value}
                  tabIndex={-1}
                  disabled={disabled}
                  onClick={() => {
                    close(true);
                    onChange(item.value);
                  }}
                >
                  <span className="language-option-copy">
                    <span lang={item.tag} dir="auto">
                      {item.label}
                    </span>
                  </span>
                  {item.value === value ? <Symbol kind="check" /> : null}
                </button>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
